import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiEdit2, FiTrash2, FiPlus, FiPercent, FiTag, FiCalendar, FiX, FiCheck, FiChevronLeft } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

const DiscountManagement = () => {
  const [discounts, setDiscounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    percentage: '',
    categories: [],
    isActive: true,
    startDate: '',
    endDate: ''
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    loadDiscounts();
    loadCategories();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadDiscounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/discounts`);
      setDiscounts(response.data.data || []);
    } catch (error) {
      toast.error('Không thể tải danh sách khuyến mãi');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const openModal = (discount = null) => {
    if (discount) {
      setEditingDiscount(discount);
      setFormData({
        name: discount.name,
        description: discount.description || '',
        percentage: discount.percentage,
        categories: discount.categories?.map(c => c._id) || [],
        isActive: discount.isActive,
        startDate: discount.startDate ? new Date(discount.startDate).toISOString().split('T')[0] : '',
        endDate: discount.endDate ? new Date(discount.endDate).toISOString().split('T')[0] : ''
      });
    } else {
      setEditingDiscount(null);
      setFormData({
        name: '',
        description: '',
        percentage: '',
        categories: [],
        isActive: true,
        startDate: new Date().toISOString().split('T')[0],
        endDate: ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        percentage: parseFloat(formData.percentage),
        categories: formData.categories.length > 0 ? formData.categories : [],
        startDate: formData.startDate || new Date(),
        endDate: formData.endDate || null
      };

      if (editingDiscount) {
        await axios.put(`${API_URL}/discounts/${editingDiscount._id}`, data);
        toast.success('Đã cập nhật khuyến mãi');
      } else {
        await axios.post(`${API_URL}/discounts`, data);
        toast.success('Đã tạo khuyến mãi');
      }
      setShowModal(false);
      loadDiscounts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể lưu khuyến mãi');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Xóa khuyến mãi này?')) return;
    try {
      await axios.delete(`${API_URL}/discounts/${id}`);
      toast.success('Đã xóa khuyến mãi');
      loadDiscounts();
    } catch (error) {
      toast.error('Không thể xóa khuyến mãi');
    }
  };

  const toggleActive = async (discount) => {
    try {
      await axios.put(`${API_URL}/discounts/${discount._id}`, {
        ...discount,
        categories: discount.categories?.map(c => c._id) || [],
        isActive: !discount.isActive
      });
      toast.success(discount.isActive ? 'Đã tắt khuyến mãi' : 'Đã bật khuyến mãi');
      loadDiscounts();
    } catch (error) {
      toast.error('Không thể cập nhật');
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId]
    }));
  };

  const isDiscountActive = (discount) => {
    if (!discount.isActive) return false;
    const now = new Date();
    const start = new Date(discount.startDate);
    const end = discount.endDate ? new Date(discount.endDate) : null;
    return now >= start && (!end || now <= end);
  };

  // Mobile Discount Card
  const MobileDiscountCard = ({ discount }) => {
    const active = isDiscountActive(discount);
    
    return (
      <div 
        className={`bg-white rounded-xl shadow-sm mx-4 mb-3 overflow-hidden border-l-4 ${
          active ? 'border-green-500' : 'border-gray-300'
        }`}
        onClick={() => openModal(discount)}
      >
        <div className="p-4">
          {/* Header row */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-base text-gray-900 truncate">{discount.name}</h3>
              {discount.description && (
                <p className="text-xs text-gray-500 truncate">{discount.description}</p>
              )}
            </div>
            <div className="flex items-center space-x-1 ml-2" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => toggleActive(discount)}
                className={`p-1.5 rounded ${discount.isActive ? 'text-green-600' : 'text-gray-400'}`}
              >
                <FiCheck size={16} />
              </button>
              <button
                onClick={() => openModal(discount)}
                className="p-1.5 rounded text-blue-600"
              >
                <FiEdit2 size={16} />
              </button>
              <button
                onClick={() => handleDelete(discount._id)}
                className="p-1.5 rounded text-red-600"
              >
                <FiTrash2 size={16} />
              </button>
            </div>
          </div>

          {/* Percentage and Status */}
          <div className="flex items-center space-x-2 mb-2">
            <div className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-sm font-bold flex items-center">
              <FiPercent className="mr-0.5" size={12} />
              {discount.percentage}%
            </div>
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}>
              {active ? 'Đang hoạt động' : 'Không hoạt động'}
            </span>
          </div>

          {/* Date and Categories */}
          <div className="text-xs text-gray-500 space-y-0.5">
            <div className="flex items-center">
              <FiCalendar className="mr-1.5" size={12} />
              {new Date(discount.startDate).toLocaleDateString('vi-VN')}
              {discount.endDate ? ` - ${new Date(discount.endDate).toLocaleDateString('vi-VN')}` : ' - Không giới hạn'}
            </div>
            <div className="flex items-center">
              <FiTag className="mr-1.5" size={12} />
              {discount.categories?.length > 0 
                ? discount.categories.map(c => c.name).join(', ')
                : 'Tất cả danh mục'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={isMobile ? "-m-4 bg-gray-50 pb-4" : "p-6"}>
      {/* Header */}
      {isMobile ? (
        <div className="bg-white px-4 py-3 flex items-center justify-between shadow-sm mb-3">
          <h1 className="text-lg font-bold text-gray-800">Quản lý Khuyến mãi</h1>
          <button
            onClick={() => openModal()}
            className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm"
          >
            <FiPlus size={16} />
            <span>Tạo khuyến mãi</span>
          </button>
        </div>
      ) : (
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Quản lý Khuyến mãi</h1>
          <button
            onClick={() => openModal()}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
          >
            <FiPlus className="mr-2" /> Tạo khuyến mãi
          </button>
        </div>
      )}

      {/* Info */}
      <div className={`bg-blue-50 border border-blue-200 rounded-lg p-3 ${isMobile ? 'mx-4 mb-3' : 'mb-6'}`}>
        <p className={`text-blue-800 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          <strong>Lưu ý:</strong> Nếu có nhiều khuyến mãi cùng áp dụng, hệ thống sẽ tự động chọn khuyến mãi có % giảm cao nhất cho khách hàng.
          Để tạo khuyến mãi riêng cho khách hàng cụ thể, vào <strong>Quản lý Khách hàng</strong> và chỉnh sửa thông tin khách.
        </p>
      </div>

      {/* Discount List */}
      {loading ? (
        <div className="text-center py-8">Đang tải...</div>
      ) : discounts.length === 0 ? (
        <div className={`bg-white rounded-lg shadow p-8 text-center text-gray-500 ${isMobile ? 'mx-4' : ''}`}>
          Chưa có khuyến mãi nào
        </div>
      ) : isMobile ? (
        // Mobile: Card list
        <div className="pb-20">
          {discounts.map(discount => (
            <MobileDiscountCard key={discount._id} discount={discount} />
          ))}
        </div>
      ) : (
        // Desktop: Grid
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {discounts.map(discount => (
            <div 
              key={discount._id} 
              className={`bg-white rounded-lg shadow p-4 border-l-4 ${
                isDiscountActive(discount) ? 'border-green-500' : 'border-gray-300'
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-bold text-lg">{discount.name}</h3>
                  {discount.description && (
                    <p className="text-gray-500 text-sm">{discount.description}</p>
                  )}
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => toggleActive(discount)}
                    className={`p-2 rounded ${discount.isActive ? 'text-green-600 hover:bg-green-100' : 'text-gray-400 hover:bg-gray-100'}`}
                    title={discount.isActive ? 'Đang bật' : 'Đang tắt'}
                  >
                    <FiCheck />
                  </button>
                  <button
                    onClick={() => openModal(discount)}
                    className="text-blue-600 hover:bg-blue-100 p-2 rounded"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    onClick={() => handleDelete(discount._id)}
                    className="text-red-600 hover:bg-red-100 p-2 rounded"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-4 mb-3">
                <div className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-lg font-bold flex items-center">
                  <FiPercent className="mr-1" />
                  {discount.percentage}%
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  isDiscountActive(discount) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  {isDiscountActive(discount) ? 'Đang hoạt động' : 'Không hoạt động'}
                </span>
              </div>

              <div className="text-sm text-gray-600 space-y-1">
                <div className="flex items-center">
                  <FiCalendar className="mr-2" size={14} />
                  {new Date(discount.startDate).toLocaleDateString('vi-VN')}
                  {discount.endDate && ` - ${new Date(discount.endDate).toLocaleDateString('vi-VN')}`}
                  {!discount.endDate && ' - Không giới hạn'}
                </div>
                <div className="flex items-center">
                  <FiTag className="mr-2" size={14} />
                  {discount.categories?.length > 0 
                    ? discount.categories.map(c => c.name).join(', ')
                    : 'Tất cả danh mục'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 z-[100] ${isMobile ? '' : 'flex items-center justify-center p-4'}`}>
          <div className={`bg-white ${isMobile ? 'fixed inset-0 overflow-y-auto' : 'rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto'}`}>
            {/* Modal Header */}
            <div className={`flex justify-between items-center sticky top-0 bg-white z-10 ${isMobile ? 'px-4 py-3 border-b shadow-sm' : 'mb-4'}`}>
              {isMobile && (
                <button onClick={() => setShowModal(false)} className="p-1 -ml-1">
                  <FiChevronLeft size={24} />
                </button>
              )}
              <h2 className={`font-bold ${isMobile ? 'text-lg flex-1 text-center' : 'text-xl'}`}>
                {editingDiscount ? 'Sửa khuyến mãi' : 'Tạo khuyến mãi mới'}
              </h2>
              {isMobile ? (
                <button 
                  type="button"
                  onClick={() => document.getElementById('discount-form').requestSubmit()}
                  className="text-green-600 font-semibold text-sm"
                >
                  Lưu
                </button>
              ) : (
                <button onClick={() => setShowModal(false)}>
                  <FiX size={24} />
                </button>
              )}
            </div>

            <form id="discount-form" onSubmit={handleSubmit} className={`space-y-4 ${isMobile ? 'p-4 pb-24' : ''}`}>
              <div>
                <label className={`block font-semibold ${isMobile ? 'text-sm mb-1' : 'mb-2'}`}>Tên khuyến mãi *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full border rounded-lg ${isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-2'}`}
                  placeholder="VD: Giảm giá cuối tuần"
                  required
                />
              </div>

              <div>
                <label className={`block font-semibold ${isMobile ? 'text-sm mb-1' : 'mb-2'}`}>Phần trăm giảm giá *</label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.percentage}
                    onChange={(e) => setFormData({ ...formData, percentage: e.target.value })}
                    className={`w-full border rounded-lg pr-10 ${isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-2'}`}
                    placeholder="15"
                    min="0"
                    max="100"
                    required
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                </div>
              </div>

              {/* Date fields - Side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block font-semibold ${isMobile ? 'text-sm mb-1' : 'mb-2'}`}>Ngày bắt đầu</label>
                  <div className="relative">
                    <input
                      type="text"
                      readOnly
                      value={formData.startDate ? (() => {
                        const [y, m, d] = formData.startDate.split('-');
                        return `${d}/${m}/${y}`;
                      })() : ''}
                      onClick={(e) => e.target.nextSibling.showPicker()}
                      placeholder="Ngày/Tháng/Năm"
                      className={`w-full border rounded-lg cursor-pointer placeholder:text-gray-300 placeholder:text-xs ${isMobile ? 'px-2 py-2 text-sm' : 'px-4 py-2'}`}
                    />
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </div>
                </div>
                <div>
                  <label className={`block font-semibold ${isMobile ? 'text-sm mb-1' : 'mb-2'}`}>Ngày kết thúc</label>
                  <div className="relative">
                    <input
                      type="text"
                      readOnly
                      value={formData.endDate ? (() => {
                        const [y, m, d] = formData.endDate.split('-');
                        return `${d}/${m}/${y}`;
                      })() : ''}
                      onClick={(e) => e.target.nextSibling.showPicker()}
                      placeholder="Ngày/Tháng/Năm"
                      className={`w-full border rounded-lg cursor-pointer placeholder:text-gray-300 placeholder:text-xs ${isMobile ? 'px-2 py-2 text-sm' : 'px-4 py-2'}`}
                    />
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">Bỏ trống = không giới hạn</p>
                </div>
              </div>

              <div>
                <label className={`block font-semibold ${isMobile ? 'text-sm mb-1' : 'mb-2'}`}>Mô tả</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={`w-full border rounded-lg ${isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-2'}`}
                  rows={2}
                  placeholder="Mô tả chi tiết..."
                />
              </div>

              <div>
                <label className={`block font-semibold ${isMobile ? 'text-sm mb-1' : 'mb-2'}`}>Áp dụng cho danh mục</label>
                <p className="text-xs text-gray-500 mb-2">Bỏ trống = áp dụng cho tất cả</p>
                <div className={`grid gap-2 max-h-32 overflow-y-auto border rounded-lg p-2 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {categories.map(category => (
                    <label key={category._id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={formData.categories.includes(category._id)}
                        onChange={() => handleCategoryToggle(category._id)}
                        className="w-4 h-4 text-green-600 rounded"
                      />
                      <span className={isMobile ? 'text-sm' : 'text-sm'}>{category.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-green-600 rounded"
                />
                <label htmlFor="isActive" className={isMobile ? 'text-sm' : 'font-semibold'}>Kích hoạt ngay</label>
              </div>

              {/* Delete button - Mobile only, edit mode only */}
              {isMobile && editingDiscount && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Xóa khuyến mãi này?')) {
                      handleDelete(editingDiscount._id);
                      setShowModal(false);
                    }
                  }}
                  className="w-full py-2.5 text-red-600 font-medium border border-red-200 rounded-lg mt-4"
                >
                  Xóa khuyến mãi này
                </button>
              )}

              {/* Desktop buttons */}
              {!isMobile && (
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-100"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                  >
                    {editingDiscount ? 'Cập nhật' : 'Tạo'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountManagement;
